import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { PUBLIC_URL } from "../../app.config";
import SubNavbar from "../../layout/SubNavbar";
import { setLoader } from "../../Store/slices/MasterSlice";
import {editBlogApiCall } from "../../Store/slices/blogSlice";
import { uploadImageOnAWS } from "../../utils/aws.service";
import { convertToBase64, TOAST_ERROR, SWIT_SUCCESS, SWIT_ERROR } from "../../utils/common.service";
import { IoMdArrowRoundBack } from "react-icons/io";
import * as API from "../../utils/api.service";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export default function BlogForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const blogData = location?.state?.blogData;  // Used for Edit mode

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    clearErrors,
  } = useForm();

  const [blogImageState, setBlogImageState] = useState(
    blogData?.blog_image || PUBLIC_URL + "/assets/images/add-photo.png"
  );
  const [description, setDescription] = useState(blogData?.description || "");
  const [arDescription, setArDescription] = useState(blogData?.ar_description || "");

  useEffect(() => {
    if (blogData) {
      setBlogImageState(blogData?.blog_image || blogImageState);
      reset({
        name: blogData?.title || "",
        arebic_title: blogData?.ar_title || "",
      });
    }
  }, [blogData, reset]);

  const handleImageChange = async (image, field) => {
    setValue(field, image);
    const bufferImage = await convertToBase64(image);
    setBlogImageState(bufferImage);
    clearErrors(field);
  };

  const onSubmit = async (submitData) => {
    try {
      dispatch(setLoader(true));

      const { name, arebic_title, image } = submitData;
      let requestBody = {
        title: name,
        ar_title: arebic_title,
        description:description,
        ar_description:arDescription,
      };

      let blogImage = image;
      if (image instanceof Blob) {
        blogImage = await uploadImageOnAWS(image, "blog");
        requestBody.image = blogImage;
      }

      if (blogData) {
        // Edit existing blog
        requestBody.id = blogData?.id;
        await dispatch(editBlogApiCall(requestBody));
        SWIT_SUCCESS("Blog updated successfully");
      } else {
        // Add new blog
        const { code, message } = await API.insertBlogApi(requestBody);
        if(code==1){
          SWIT_SUCCESS("Blog added successfully");
        }
        else{
          TOAST_ERROR("Something went wrong when adding blog");
        }
      }

      reset();
      navigate("/manage_blog");
      dispatch(setLoader(false));
    } catch (err) {
      TOAST_ERROR(err.message);
      dispatch(setLoader(false));
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5] }, { font: [] }],
      [{ size: ['small', 'medium', 'large', 'huge'] }],
      ["bold", "underline", "italic", "strike", "blockquote"],
      ["code-block", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  return (
    <div className="content">
      <div className="container-fluid">
        <SubNavbar title={blogData ? "Edit Blog" : "Add Blog"} header="Blog" subHeader={blogData ? "Edit Blog" : "Add Blog"} />
        <div className="card rounded-3">
          <div className="card-body">
            <div className="d-flex justify-content-lg-start">
              <button type="button" className="btn btn-light" onClick={() => navigate(-1)}>
                <IoMdArrowRoundBack fontSize={"20px"} />
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row mb-3">
                <Controller
                  name="image"
                  control={control}
                  defaultValue={null}
                  rules={!blogData && { required: "Please choose a blog image" }} // Only validate in add mode
                  render={({ field }) => (
                    <div className="col-md-4 text-center mx-auto">
                      {blogImageState && (
                        <img
                          src={blogImageState}
                          alt="blog image"
                          className="img-fluid img-thumbnail"
                          style={{ width: "120px", borderRadius: "10%" }}
                        />
                      )}
                      <input
                        type="file"
                        style={{ display: "none" }}
                        id="blog_image"
                        accept=".jpg, .jpeg, .png"
                        onChange={(e) =>
                          handleImageChange(e.target.files[0], "image")
                        }
                      />
                      <label htmlFor="blog_image" className="btn btn-outline-info m-1">
                        Choose Blog Image
                      </label>
                      {/* Show validation message only in add mode */}
                      {!blogData && <div className="errorc">{errors.image?.message}</div>}
                    </div>
                  )}
                />
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="name" className="form-label">Blog Title</label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    placeholder="Enter blog title"
                    {...register("name", { required: "Blog Name is required" })}
                  />
                  <div className="errorc">{errors.name?.message}</div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="arebic_title" className="form-label">Blog Arebic Title</label>
                  <input
                    type="text"
                    id="arebic_title"
                    className="form-control"
                    placeholder="Enter Blog Arebic Title"
                    {...register("arebic_title", { required: "Blog Arebic Title is required" })}
                  />
                  <div className="errorc">{errors.arebic_title?.message}</div>
                </div>
              </div>

              <h3 className="d-flex justify-content-start pt-3 px-3">
                Description (English) <b style={{ color: "red" }}>*</b>
              </h3>
              <ReactQuill
                type="text"
                id="description"
                modules={modules}
                theme="snow"
                value={description}
                onChange={setDescription}
                className="mb-4"
                style={{ height: "430px" }}
              />

              <h3 className="d-flex justify-content-start pt-3 px-3">
                Description (Arabic) <b style={{ color: "red" }}>*</b>
              </h3>
              <ReactQuill
                type="text"
                id="ar_description"
                modules={modules}
                theme="snow"
                value={arDescription}
                onChange={setArDescription}
                style={{ height: "430px" }}
              />

              <div className="mb-0 text-center mt-5">
                <button className="btn btn-soft-primary w-25 buttonColor" type="submit">
                  <i className="ri-save-fill"></i> <span className="fw-bold">{blogData ? "Update" : "Add"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
